import type { JSX } from "react";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import HelpCircle from "@repo/icons/default/HelpCircle";
import Left from "@repo/icons/default/Left";
import { graphql } from "@repo/graphql-types/gql";
import { z } from "zod";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { BillingSummary } from "@/components/billing/billing-summary";
import { BillingActions } from "@/components/billing/actions/billing-actions";
import { validatePjAccess } from "@/lib/user-pj-guard.ts";

const NewBillingItemDetailsPageQuery = graphql(`
  query NewBillingItemDetailsPageQuery($billingId: Int!) {
    cobrancasFechadas(where: { codCobranca: { eq: $billingId } }) {
      billings: nodes {
        ...BillingSummaryQueryFragment
      }
    }
    ...BillingActionQueryFragment
  }
`);

const NewBillingItemDetailsPage = (): JSX.Element => {
  const router = useRouter();
  const handleAbout = (): void => {
    void router.navigate({ to: "/settings/billing/about" });
  };
  const { billingId } = useParams({ from: "/settings/billing/details/$billingId/" });

  const queryData = useGraphQL(NewBillingItemDetailsPageQuery, {
    billingId,
  });

  const { data } = queryData;
  const billingData = data?.cobrancasFechadas?.billings?.[0];

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Forma de pagamento" align="center" />
        <HeaderButton icon={HelpCircle} onClick={handleAbout} align="end" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryData}>
          {data?.cobrancasFechadas ? (
            <div className="flex flex-col gap-6">
              {billingData ? <BillingSummary data={billingData} /> : null}
              <BillingActions billingId={billingId} data={data} />
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/billing/details/$billingId/")({
  component: NewBillingItemDetailsPage,
  parseParams: (params) => ({
    billingId: z.int().parse(Number(params.billingId)),
  }),
  beforeLoad: ({ params, location, context }) => {
    const { user } = context;
    validatePjAccess({ location, user, billingId: params.billingId });
  },
});
